import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { Plus, Edit2, Trash2, X, LogOut, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { issueService, articleService, authService } from '../services/api';

function AdminIssues() {
  const navigate = useNavigate();
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchIssues();
  }, []);

  const fetchIssues = async () => {
    try {
      setLoading(true);
      const response = await issueService.getForManagement();
      setIssues(response.data);
    } catch (err) {
      setError('خطا در بارگذاری شماره‌ها');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const [showIssueForm, setShowIssueForm] = useState(false);
  const [editingIssueId, setEditingIssueId] = useState(null);
  const [issueForm, setIssueForm] = useState({
    volume: 1,
    number: '',
    title: '',
    description: '',
    publicationDate: '',
  });

  const [expandedIssue, setExpandedIssue] = useState(null);
  const [showArticleForm, setShowArticleForm] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: '',
    abstract: '',
    keywords: '',
    content: '',
    status: 'Draft',
  });

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  // Issue handlers
  const handleAddIssue = async (e) => {
    e.preventDefault();
    
    try {
      const issueData = {
        ...issueForm,
        publicationDate: new Date(issueForm.publicationDate).toISOString(),
      };

      if (editingIssueId) {
        await issueService.update(editingIssueId, issueData);
        setIssues(issues.map(issue =>
          issue.id === editingIssueId
            ? { ...issue, ...issueData }
            : issue
        ));
        setEditingIssueId(null);
      } else {
        const response = await issueService.create(issueData);
        setIssues([...issues, response.data]);
      }

      setIssueForm({
        volume: 1,
        number: '',
        title: '',
        description: '',
        publicationDate: '',
      });
      setShowIssueForm(false);
    } catch (err) {
      console.error('Error saving issue:', err);
      alert('خطا در ذخیره شماره');
    }
  };

  const handleEditIssue = (issue) => {
    setIssueForm({
      volume: issue.volume,
      number: issue.number,
      title: issue.title,
      description: issue.description,
      publicationDate: issue.publicationDate ? issue.publicationDate.split('T')[0] : '',
    });
    setEditingIssueId(issue.id);
    setShowIssueForm(true);
  };

  const handleDeleteIssue = async (id) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      try {
        await issueService.delete(id);
        setIssues(issues.filter(i => i.id !== id));
      } catch (err) {
        console.error('Error deleting issue:', err);
        alert('خطا در حذف شماره');
      }
    }
  };

  // Article handlers
  const handleAddArticle = async (issueId) => {
    try {
      const articleData = {
        ...articleForm,
        issueId: issueId,
        submissionDate: new Date().toISOString(),
      };

      const response = await articleService.create(articleData);
      
      setIssues(issues.map(issue =>
        issue.id === issueId
          ? {
              ...issue,
              articles: [...(issue.articles || []), response.data],
            }
          : issue
      ));

      setArticleForm({
        title: '',
        abstract: '',
        keywords: '',
        content: '',
        status: 'Draft',
      });
      setShowArticleForm(null);
    } catch (err) {
      console.error('Error saving article:', err);
      alert('خطا در ذخیره مقاله');
    }
  };

  const handleDeleteArticle = async (issueId, articleId) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      try {
        await articleService.delete(articleId);
        setIssues(issues.map(issue =>
          issue.id === issueId
            ? {
                ...issue,
                articles: issue.articles.filter(a => a.id !== articleId),
              }
            : issue
        ));
      } catch (err) {
        console.error('Error deleting article:', err);
        alert('خطا در حذف مقاله');
      }
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-5xl font-bold mb-2">مدیریت شماره‌های نشریه</h1>
              <p className="text-xl text-blue-100">افزودن و مدیریت شماره‌ها، جلدها و مطالب</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition"
            >
              <LogOut size={20} />
              خروج
            </button>
          </div>
        </div>
      </div>

      <AdminNav />

      <div className="max-w-7xl mx-auto px-4 py-16">
        {loading && <div className="text-center py-12 text-gray-500">در حال بارگذاری...</div>}
        {error && <div className="bg-red-100 text-red-700 p-4 rounded-lg mb-8">{error}</div>}

        {/* Add Issue Button */}
        {!showIssueForm && !loading && (
          <button
            onClick={() => setShowIssueForm(true)}
            className="mb-8 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition shadow-lg"
          >
            <Plus size={20} />
            افزودن شماره جدید
          </button>
        )}

        {/* Issue Form */}
        {showIssueForm && (
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingIssueId ? 'ویرایش شماره' : 'افزودن شماره جدید'}
              </h2>
              <button
                onClick={() => {
                  setShowIssueForm(false);
                  setEditingIssueId(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={24} className="text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleAddIssue} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">جلد</label>
                  <input
                    type="number"
                    value={issueForm.volume}
                    onChange={(e) => setIssueForm({ ...issueForm, volume: parseInt(e.target.value) })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">شماره</label>
                  <input
                    type="number"
                    value={issueForm.number}
                    onChange={(e) => setIssueForm({ ...issueForm, number: parseInt(e.target.value) })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">عنوان</label>
                <input
                  type="text"
                  value={issueForm.title}
                  onChange={(e) => setIssueForm({ ...issueForm, title: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  placeholder="عنوان شماره"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">توضیح</label>
                <textarea
                  value={issueForm.description}
                  onChange={(e) => setIssueForm({ ...issueForm, description: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  placeholder="توضیح شماره"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-900 mb-2">تاریخ انتشار</label>
                <input
                  type="date"
                  value={issueForm.publicationDate}
                  onChange={(e) => setIssueForm({ ...issueForm, publicationDate: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div className="flex gap-4 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-bold hover:from-blue-700 hover:to-blue-800 transition"
                >
                  {editingIssueId ? 'ذخیره تغییرات' : 'افزودن شماره'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowIssueForm(false);
                    setEditingIssueId(null);
                  }}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                >
                  انصراف
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Issues List */}
        <div className="space-y-6">
          {issues.map(issue => (
            <div key={issue.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
              {/* Issue Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2">{issue.title}</h3>
                    <p className="text-blue-100">جلد {issue.volume} - شماره {issue.number}</p>
                    <p className="text-blue-100 text-sm mt-2">{issue.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditIssue(issue)}
                      className="p-3 bg-blue-500 hover:bg-blue-600 rounded-lg transition"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteIssue(issue.id)}
                      className="p-3 bg-red-600 hover:bg-red-700 rounded-lg transition"
                    >
                      <Trash2 size={18} />
                    </button>
                    <button
                      onClick={() => setExpandedIssue(expandedIssue === issue.id ? null : issue.id)}
                      className="p-3 bg-blue-500 hover:bg-blue-600 rounded-lg transition"
                    >
                      {expandedIssue === issue.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Articles Section */}
              {expandedIssue === issue.id && (
                <div className="p-6 border-t border-gray-200">
                  {/* Add Article Button */}
                  {showArticleForm !== issue.id && (
                    <button
                      onClick={() => setShowArticleForm(issue.id)}
                      className="mb-6 flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition"
                    >
                      <Plus size={16} />
                      افزودن مقاله جدید
                    </button>
                  )}

                  {/* Article Form */}
                  {showArticleForm === issue.id && (
                    <div className="bg-gray-50 rounded-lg p-6 mb-6 border border-gray-200">
                      <h4 className="font-bold text-gray-900 mb-4">افزودن مقاله جدید</h4>
                      <div className="space-y-4 mb-4">
                        <input
                          type="text"
                          placeholder="عنوان مقاله"
                          value={articleForm.title}
                          onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                          required
                        />
                        <textarea
                          placeholder="چکیده"
                          value={articleForm.abstract}
                          onChange={(e) => setArticleForm({ ...articleForm, abstract: e.target.value })}
                          rows="3"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                        />
                        <input
                          type="text"
                          placeholder="کلمات کلیدی"
                          value={articleForm.keywords}
                          onChange={(e) => setArticleForm({ ...articleForm, keywords: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                        />
                        <textarea
                          placeholder="محتوای مقاله"
                          value={articleForm.content}
                          onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                          rows="5"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                        />
                        <select
                          value={articleForm.status}
                          onChange={(e) => setArticleForm({ ...articleForm, status: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                        >
                          <option value="Draft">پیش‌نویس</option>
                          <option value="Under Review">در حال بررسی</option>
                          <option value="Published">منتشر شده</option>
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAddArticle(issue.id)}
                          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
                        >
                          افزودن مقاله
                        </button>
                        <button
                          onClick={() => setShowArticleForm(null)}
                          className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition"
                        >
                          انصراف
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Articles List */}
                  <div className="space-y-3">
                    {(issue.articles || []).map(article => (
                      <div key={article.id} className="bg-white rounded-lg p-4 border border-gray-200 flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-bold text-gray-900">{article.title}</p>
                          <p className="text-sm text-gray-600 mt-1">{article.abstract}</p>
                          <p className="text-xs text-gray-500 mt-2">وضعیت: {article.status}</p>
                        </div>
                        <button
                          onClick={() => handleDeleteArticle(issue.id, article.id)}
                          className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>

                  {(!issue.articles || issue.articles.length === 0) && showArticleForm !== issue.id && (
                    <p className="text-gray-500 italic">هیچ مقاله‌ای برای نمایش وجود ندارد</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {issues.length === 0 && !showIssueForm && (
          <div className="text-center py-12 text-gray-500">
            <FileText size={48} className="mx-auto mb-4 opacity-30" />
            <p>هیچ شماره‌ای برای نمایش وجود ندارد</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminIssues;
